/**
 * Boot factory for same-pod spawned children (session:spawnTui). Keeps the
 * CLI entry lean: given the env-derived identity, constructs the right runner
 * — an interactive Claude TUI (mode "adhoc" WITH a task id, unlike the
 * task-less Sessions-view adhoc pods) or a raw login shell (mode "shell").
 */
import { createServiceLogger } from "../utils/logger.js";
import { AdhocSessionRunner } from "./adhoc-session-runner.js";
import { ShellSessionRunner } from "./shell-session-runner.js";

const logger = createServiceLogger("SpawnedChildBoot");

export interface SpawnedChildInputs {
  mode: "adhoc" | "shell";
  apiUrl: string;
  taskToken: string;
  /** The spawned WorkspaceSession id (CONVEYOR_SESSION_ID on the child). */
  sessionId: string;
  workspaceDir: string;
  /** For the adhoc runner's connected-event payload (CONVEYOR_PROJECT_ID). */
  projectId: string;
}

export interface SpawnedChildRunner {
  run(): Promise<void>;
  stop(): void;
  readonly finalState: "finished" | "error" | null;
}

export function createSpawnedChildRunner(inputs: SpawnedChildInputs): SpawnedChildRunner {
  if (inputs.mode === "shell") {
    return new ShellSessionRunner({
      connection: {
        apiUrl: inputs.apiUrl,
        taskToken: inputs.taskToken,
        sessionId: inputs.sessionId,
        runnerMode: "shell",
      },
      workspaceDir: inputs.workspaceDir,
    });
  }
  return new AdhocSessionRunner(
    {
      connection: {
        apiUrl: inputs.apiUrl,
        taskToken: inputs.taskToken,
        sessionId: inputs.sessionId,
        runnerMode: "adhoc",
      },
      projectId: inputs.projectId,
      // Session lineage key. NOT the inherited CONVEYOR_WORKSPACE_ID — that's
      // the builder's lineage; concurrent tabs must each be their own Claude
      // conversation, so key on the spawned session id.
      workspaceId: inputs.sessionId,
      workspaceDir: inputs.workspaceDir,
    },
    {
      onEvent: (event) => {
        logger.info("Spawned TUI event", { eventType: event.type as string });
      },
    },
  );
}
